import { Box } from '@amzn/meridian';
import type { FormContextType, RJSFSchema, StrictRJSFSchema, WidgetProps } from '@rjsf/utils';
import { get } from 'es-toolkit/compat';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useCampaignConfig } from '../../../../../hooks/useCampaignConfig';
import { useRequiredFieldOnChange } from '../utils/useRequiredFieldOnChange';
import { LinkPreview } from './components/LinkPreview';
import { RefTagInput } from './components/RefTagInput';
import { useAutoGeneratedRefTag } from './hooks/useAutoGeneratedRefTag';
import { appendRefTagToUrl, parseUrl } from './utils';
import { getCreativeName, isValidFormContext } from './utils/formContextUtils';
import { generateRefTag } from './utils/refTagsUtils';

/**
 * Extracts field value from RJSF formContext using a field path.
 * Returns empty string if formContext is invalid or field doesn't exist.
 */
const getFieldValueFromFormContext = (formContext: unknown, fieldPath: string): string => {
  if (!isValidFormContext(formContext)) {
    return '';
  }

  const currentFlavor = formContext.flavors[formContext.currentFlavorIndex];
  const formData = currentFlavor?.content?.arguments;

  if (!formData) {
    return '';
  }

  const result = get(formData, fieldPath);
  return typeof result === 'string' ? result : '';
};

/**
 * LinkRefTagBuilder - Manages refTag field only
 * Reads sibling URL field to show complete preview with refTag appended
 * Used in split-field schemas where URL and refTag are separate fields
 * Stores plain refTag string (e.g., 'test123')
 */
export const LinkRefTagBuilder = <T = never, S extends StrictRJSFSchema = RJSFSchema, F extends FormContextType = never>(
  props: WidgetProps<T, S, F>,
) => {
  const {
    onChange,
    value,
    registry: { formContext },
    uiSchema,
    required,
  } = props;

  const requiredFieldOnChange = useRequiredFieldOnChange(onChange, required);
  const lastSentRefTagRef = useRef<string>(value || '');
  const [refTag, setRefTag] = useState<string>(value || '');

  const campaignConfig = useCampaignConfig();

  const creativeName = useMemo(
    () => getCreativeName(formContext),
    [
      formContext,
    ],
  );

  const [isAutoGenEnabled, setIsAutoGenEnabled] = useState<boolean>(() => {
    const existingRefTag = value || '';

    if (!existingRefTag) {
      return true;
    }

    if (!creativeName) {
      return false;
    }

    return existingRefTag === generateRefTag(creativeName, campaignConfig.pageType);
  });

  const urlFieldPath = useMemo(() => {
    const uiOptions = uiSchema?.['ui:options'] as Record<string, unknown> | undefined;
    return (uiOptions?.urlFieldName as string) || 'redirectPath';
  }, [
    uiSchema,
  ]);

  const siblingUrl = useMemo(
    () => getFieldValueFromFormContext(formContext, urlFieldPath),
    [
      formContext,
      urlFieldPath,
    ],
  );

  const parsedUrl = useMemo(
    () => parseUrl(siblingUrl, formContext?.platform),
    [
      siblingUrl,
      formContext?.platform,
    ],
  );

  const previewUrl = useMemo(() => {
    if (!siblingUrl) return '';
    return appendRefTagToUrl(siblingUrl, parsedUrl.linkType, refTag);
  }, [
    siblingUrl,
    parsedUrl.linkType,
    refTag,
  ]);

  const handleRefTagChange = useCallback((newRefTag: string) => setRefTag(newRefTag), []);

  const handleAutoGenChange = useCallback((checked: boolean) => {
    setIsAutoGenEnabled(checked);
    if (!checked) setRefTag('');
  }, []);

  useAutoGeneratedRefTag({
    creativeName,
    isAutoGenEnabled,
    pageType: campaignConfig.pageType,
    setRefTag,
  });

  useEffect(() => {
    if (refTag !== lastSentRefTagRef.current) {
      lastSentRefTagRef.current = refTag;
      requiredFieldOnChange(refTag);
    }
  }, [
    requiredFieldOnChange,
    refTag,
  ]);

  return (
    <Box>
      <RefTagInput isAutoGenEnabled={isAutoGenEnabled} onAutoGenChange={handleAutoGenChange} onChange={handleRefTagChange} value={refTag} />
      <Box spacingInset="400 none none">
        <LinkPreview marketplace={formContext?.marketplace} previewUrl={previewUrl} />
      </Box>
    </Box>
  );
};
